import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Row, Col, Tag, message, Card, ColorPicker } from 'antd';
import { PlusOutlined, EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons';
import axios from '../../config/axiosInstance';
import { useAuth } from '../../context/AuthContext';

import ModuleClientRegasus from './Client/ModuleClientRegasus';
import ModuleClientFeatures from './Client/ModuleClientFeatures';

const AddEditClient = ({ visible, onClose, client, onSave }) => {
  const [form] = Form.useForm();
  const { refreshUser } = useAuth?.() || { refreshUser: () => {} };
  const [hosts, setHosts] = useState([]);
  const [hostInput, setHostInput] = useState('');
  const [activeTabKey, setActiveTabKey] = useState('general');
  const [portalLogoURL, setPortalLogoURL] = useState('');

  // 6-digit random PIN generation function
  const generateRandomPin = () => {
    return Math.floor(100000 + Math.random() * 900000).toString(); 
  };

  useEffect(() => {
    if (client) {
      // client, set initial values
      // If the customer already has a PIN, it is used. If not, it generates a new one.
      const initialPin = client.devicePin || generateRandomPin();

      form.setFieldsValue({
        name: client.name || '',
        username: client.username || '',
        password: client.password || '',
        portalName: client.portalName || '',
        portalLogo: client.portalLogo || '',
        clientColor: client.clientColor || '#1677ff',
        devicePin: initialPin,
        regasusDownloadUrl: client.regasusDownloadUrl || undefined,
        features: client.features || [],
      });
      setPortalLogoURL(client.portalLogo || '');
      setHosts(client.hosts || []);
    } else {
      // Not client, reset the form
      const newPin = generateRandomPin();
      
      form.resetFields();
      setPortalLogoURL('');
      setHosts([]);
      form.setFieldsValue({ devicePin: newPin , features: [] });
    }
  }, [client, form]);

  const handleAddHost = () => {
    if (hostInput && !hosts.includes(hostInput)) {
      setHosts((prevHosts) => [...prevHosts, hostInput]);
      setHostInput('');
    }
  };

  const handleRemoveHost = (host) => {
    setHosts((prevHosts) => prevHosts.filter((h) => h !== host));
  };

  const handleFinish = async (values) => {
    try {
      // Transform clientColor a string (e.g., #RRGGBB o #RRGGBBAA)
      const clientColor = values.clientColor?.toHexString
      ? values.clientColor.toHexString()
      : values.clientColor;

      const devicePin = values.devicePin ? values.devicePin.replace(/\D/g, '') : '';
      const regasusDownloadUrl = values.regasusDownloadUrl ?? null;
      const features = values.features || [];

      const payload = { ...values, hosts, clientColor, devicePin, regasusDownloadUrl, features };

      if (client) {
        // Upgrade existing customer
        await axios.put(`/clients/${client._id}`, payload);
        message.success('Client updated successfully');
      } else {
        // Create a new customer
        await axios.post('/clients', payload);
        message.success('Client added successfully');
      }
      refreshUser(); // Pick up changed client data (e.g. features) without a page reload
      onSave(); // Notifying the parent component that it was saved successfully
      onClose(); // Close the modal
    } catch (error) {
      message.error('Error saving client. Please try again.');
    }
  };

  const tabList = [
    {
      key: 'general',
      tab: 'General',
    },
    {
      key: 'portal',
      tab: 'Portal',
    },
    {
      key: 'regasus',
      tab: 'Regasus',
    },
    {
      key: 'features',
      tab: 'Features',
    }
  ];

  const contentList = {
    general: (
      <>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
            name="name"
            label="Client Name"
            rules={[{ required: true, message: 'Please input the client name!' }]}
            >
              <Input placeholder="Enter client name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="devicePin"
              label="Customer PIN"
              rules={[
                { required: false, message: 'Please enter the Device PIN' },
                { pattern: /^[0-9]{6}$/, message: 'The PIN must be exactly 6 digits' },
              ]}
            >
              <Input.OTP
                length={6} // Only 6 characters allowed
                formatter={(str) => str.replace(/\D/g, '')} // Allows only numbers
                variant='filled'
              />
            </Form.Item>
          </Col>
        </Row>    
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: 'Please input the username!' }]}
            >
              <Input placeholder="Enter username" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please input the password!' }]}
            >
              <Input.Password
                placeholder="Enter password"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Add Hosts">
          <Row gutter={16}>
            <Col span={8}>
              <Input
                placeholder="Enter host"
                value={hostInput}
                onChange={(e) => setHostInput(e.target.value)}
              />
            </Col>
            <Col span={4}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddHost}
                disabled={!hostInput.trim()}
              >
                Add
              </Button>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {hosts.map((host) => (
                <Tag
                    key={host}
                    closable
                    onClose={() => handleRemoveHost(host)}
                    color="blue"
                >
                    {host}
                </Tag>
                ))}
              </div>
            </Col>
          </Row>
        </Form.Item>
      </>
    ),
    portal: (
      <>
        <Row gutter={[16, 16]}>
          {/* Column 1: Portal Name */}
          <Col xs={24} sm={12}>
            <Form.Item
              name="portalName"
              label="Portal Name"
              rules={[{ required: true, message: 'Please input the portal name!' }]}
            >
              <Input placeholder="Enter portal name" />
            </Form.Item>
            <Form.Item
              name="clientColor"
              label="Color"
            >
              <ColorPicker
                value={form.getFieldValue('clientColor')} 
                showText
                allowClear
                defaultFormat="hex"
              />
            </Form.Item>
          </Col>

          {/* Column 2: Portal Logo URL */}
          <Col xs={24} sm={12}>
            <Form.Item
              name="portalLogo"
              label="Portal Logo URL"
            >
              <Input
                placeholder="Enter portal logo URL"
                value={portalLogoURL}
                onChange={(e) => setPortalLogoURL(e.target.value)}
              />
            </Form.Item>
            {portalLogoURL && (
              <img
                src={portalLogoURL}
                alt="Portal Logo"
                style={{
                  maxWidth: '150px',
                  maxHeight: '150px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '4px',
                  marginTop: '8px',
                }}
              />
            )}
          </Col>
        </Row>
      </>
    ),
    regasus: <ModuleClientRegasus form={form} />,
    features: <ModuleClientFeatures form={form} />,
  };

  const onTabChange = (key) => {
    setActiveTabKey(key);
  };

  return (
      <Modal
        open={visible}
        title={client ? 'Edit Client' : 'Add New Client'}
        onCancel={onClose}
        footer={null}
        width={1000}
      >
        <Form layout="vertical" form={form} onFinish={handleFinish}>
          <Card
            style={{ width: '100%' }}
            tabList={tabList}
            activeTabKey={activeTabKey}
            onTabChange={onTabChange}
          >

            {/* Keep all tabs mounted and hide the inactive tabs. */}
            <div style={{ display: activeTabKey === 'general' ? 'block' : 'none' }}>
              {contentList.general}
            </div>
            <div style={{ display: activeTabKey === 'portal' ? 'block' : 'none' }}>
              {contentList.portal}
            </div>
            <div style={{ display: activeTabKey === 'regasus' ? 'block' : 'none' }}>
              {contentList.regasus}
            </div>
            <div style={{ display: activeTabKey === 'features' ? 'block' : 'none' }}>
              {contentList.features}
            </div>
          </Card>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', gap: '8px' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={() => form.submit()}>
              {client ? 'Save Changes' : 'Add Client'}
            </Button>
          </div>
        </Form>
      </Modal>

  );
};

export default AddEditClient;
